import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "No boot recorded yet");

const StepList = ({ steps }) => (
  <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {steps.map((s) => (
      <li key={s.name} className="flex items-start gap-2 text-sm" data-testid={`boot-step-${s.name}`}>
        {s.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#264839] shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 text-[#B35C4A] shrink-0" />}
        <div className="min-w-0">
          <div className="text-[#11211A] font-medium">{s.name.replace(/_/g, " ")}</div>
          <div className={`text-xs break-all ${s.ok ? "text-[#7D8C85]" : "text-[#B35C4A]"}`}>{s.result}</div>
        </div>
      </li>
    ))}
  </ul>
);

export const BootSummaryCard = () => {
  const [boot, setBoot] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/boot-summary").then(({ data }) => setBoot(data)).catch(() => setBoot({ at: null, steps: [], history: [] }));
  }, []);

  if (!boot) return null;
  const history = (boot.history || []).slice(1);
  const safeModeOn = (boot.steps || []).some((s) => s.name === "email_safe_mode" && !s.ok);

  return (
    <div className="mt-8 bg-white border border-[#E8ECE9] p-8" data-testid="boot-summary-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#264839]" />
          <div className="font-serif text-xl text-[#11211A]">Last startup</div>
          {boot.ok === false && <span className="text-[10px] uppercase tracking-[0.2em] text-white bg-[#B35C4A] px-2 py-0.5" data-testid="boot-failed-badge">Step failed</span>}
        </div>
        <div className="text-sm text-[#7D8C85]" data-testid="boot-summary-time">{fmt(boot.at)}</div>
      </div>
      {safeModeOn && (
        <div className="mt-4 bg-[#B35C4A] text-white px-4 py-3 text-sm flex items-start gap-2" data-testid="email-safe-mode-warning">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div><span className="font-semibold">Email safe mode is ON.</span> Every outbound email is being redirected to the safe inbox — no supplier receives mail. Fine in preview; in production set <code>EMAIL_SAFE_MODE=off</code> and redeploy.</div>
        </div>
      )}
      {boot.steps?.length > 0 && <StepList steps={boot.steps} />}

      {history.length > 0 && (
        <div className="mt-6 border-t border-[#E8ECE9] pt-4">
          <button type="button" onClick={() => setShowHistory((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-[#264839] hover:underline" data-testid="boot-history-toggle">
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Previous startups ({history.length})
          </button>
          {showHistory && (
            <ul className="mt-3 divide-y divide-[#E8ECE9]" data-testid="boot-history-list">
              {history.map((h) => (
                <li key={h.id || h.at} className="py-2">
                  <button type="button" onClick={() => setOpen(open === h.at ? null : h.at)} className="w-full flex items-center justify-between gap-3 text-left text-sm" data-testid={`boot-history-row-${h.at}`}>
                    <span className="inline-flex items-center gap-2 text-[#11211A]">
                      {h.ok ? <CheckCircle2 className="w-4 h-4 text-[#264839]" /> : <XCircle className="w-4 h-4 text-[#B35C4A]" />}
                      {fmt(h.at)}
                    </span>
                    <span className="text-xs text-[#7D8C85] truncate max-w-[60%]">{h.line}</span>
                  </button>
                  {open === h.at && <StepList steps={h.steps || []} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="text-xs text-[#7D8C85] mt-5">Written by the backend at the end of every deploy/restart — the same line as “BOOT SUMMARY” in the server log. Last 10 startups are kept.</p>
    </div>
  );
};

export default BootSummaryCard;
